import { useContext, useEffect, useState } from 'react';
import { 
  Typography, 
  TextField, 
  Button, 
  IconButton, 
  Grid, 
  Box, 
  Card, 
  CardContent, 
  Paper,
  Container,
  CircularProgress,
  Alert,
  Divider
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { Link, useNavigate } from 'react-router-dom';
import { IoMdArrowBack } from 'react-icons/io';
import { AuthContext } from '../Context/AuthContext';
import axios from 'axios';
import { toast } from 'react-toastify';
import Navbar from '../components/Navbar';

const DoctorsListPage = () => {
  const [emails, setEmails] = useState([]);
  const [txtEmail, setTxtEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const baseapiUrl = `${import.meta.env.VITE_APP_BASE_API}${import.meta.env.VITE_APP_API_VERSION}/careteam`;
  const { isAuthenticated } = useContext(AuthContext);
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated) {
      getEmailList();
    } else {
      setError("Please login to view your doctors list");
    }
  }, [isAuthenticated]);

  const getApiOption = () => {
    const token = localStorage.getItem("token");
    
    if (!token) {
      setError("Authentication token not found");
      return null;
    }
    
    return {
      headers: { Authorization: `Bearer ${token}` },
      validateStatus: status => status < 500
    };
  };

  const getEmailList = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const options = getApiOption();
      if (!options) return;
      
      console.log("Fetching doctors list...");
      
      const response = await axios.get(`${baseapiUrl}/doctor`, options);
      
      console.log("Doctors list API response:", response);
      
      if (response.status === 401) {
        setError("Authentication error. Please refresh and login again.");
        return;
      }
      
      setEmails(response.data || []);
    } catch (err) {
      console.error("Error fetching doctors list:", err);
      setError("Could not load doctors list. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const addEmail = async (emailAddress) => {
    if (!emailAddress || !emailAddress.trim()) {
      toast.warning("Please enter a valid email address");
      return;
    }
    
    try {
      const options = getApiOption();
      if (!options) return;
      
      setLoading(true);
      const response = await axios.post(
        baseapiUrl,
        { emailAddress, personType: 'doctor' },
        options
      );
      
      if (response.status === 200 && response.data.emailRecord) {
        setEmails([...emails, response.data.emailRecord]);
        toast.success("Doctor added successfully");
        setTxtEmail('');
      }
    } catch (error) {
      console.error("Error adding doctor:", error);
      toast.error(error.response?.data?.message || "Failed to add doctor");
    } finally {
      setLoading(false);
    }
  };

  const removeEmail = async (emailItem) => {
    try {
      const options = getApiOption();
      if (!options) return;
      
      const response = await axios.delete(
        `${baseapiUrl}/${emailItem._id}`,
        options
      );
      
      if (response.status === 200) {
        setEmails(emails.filter(item => item._id !== emailItem._id));
        toast.success("Doctor removed successfully");
      }
    } catch (error) {
      console.error("Error removing doctor:", error);
      toast.error(error.response?.data?.message || "Failed to remove doctor");
    }
  };

  return (
    <>
      <Navbar />
      
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Paper elevation={1} sx={{ mb: 3, p: 2, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton 
              edge="start" 
              color="primary" 
              aria-label="back to profile"
              onClick={() => navigate('/profile')}
              sx={{ mr: 2 }}
            >
              <IoMdArrowBack size={24} />
            </IconButton>
            <Typography variant="h5" fontWeight="600">
              Doctors List
            </Typography>
          </Box>
        </Paper>

        <Grid container spacing={3}>
          <Grid item xs={12} md={12}>
            <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
              <Typography variant="h6" gutterBottom>
                Manage Your Doctors
              </Typography>
              <Divider sx={{ mb: 3 }} />
              
              {error ? (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {error}
                  <Button
                    variant="text"
                    color="inherit"
                    onClick={getEmailList}
                    sx={{ ml: 2 }}
                    size="small"
                  >
                    Retry
                  </Button>
                </Alert>
              ) : (
                <>
                  <Grid container spacing={2} sx={{ mb: 4 }}>
                    <Grid item xs={12} md={8}>
                      <TextField
                        label="Doctor's Email"
                        type="email"
                        value={txtEmail}
                        onChange={(e) => setTxtEmail(e.target.value)}
                        fullWidth
                        placeholder="Enter your doctor's email address"
                        variant="outlined"
                        disabled={loading}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Button
                        variant="contained"
                        fullWidth
                        onClick={() => addEmail(txtEmail)}
                        startIcon={loading ? <CircularProgress size={20} /> : <AddIcon />}
                        disabled={loading}
                        sx={{ height: '100%' }}
                      >
                        Add Doctor
                      </Button>
                    </Grid>
                  </Grid>
                  
                  <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 2 }}>
                    Your Doctors
                  </Typography>
                  
                  {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                      <CircularProgress />
                    </Box>
                  ) : emails.length > 0 ? (
                    emails.map((email) => (
                      <Card key={email._id || email.emailAddress} sx={{ mb: 2 }}>
                        <CardContent>
                          <Grid container alignItems="center" justifyContent="space-between">
                            <Grid item>
                              <Typography variant="body1" fontWeight="medium">
                                {email.emailAddress}
                              </Typography>
                            </Grid>
                            <Grid item>
                              <IconButton 
                                onClick={() => removeEmail(email)} 
                                color="error"
                                size="small"
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Grid>
                          </Grid>
                        </CardContent>
                      </Card>
                    ))
                  ) : (
                    <Box sx={{ textAlign: 'center', py: 4, bgcolor: '#f5f5f5', borderRadius: 2 }}>
                      <Typography color="text.secondary">
                        You haven't added any doctors yet.
                      </Typography>
                      <Typography color="text.secondary" variant="body2" sx={{ mt: 1 }}>
                        Add your doctors' email addresses to share your health information with them.
                      </Typography>
                    </Box>
                  )}
                </>
              )}
              
              <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => navigate('/profile')}
                  startIcon={<IoMdArrowBack />}
                  sx={{ borderRadius: 2 }}
                >
                  Back to Profile
                </Button>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </>
  );
};

export default DoctorsListPage;
